import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { WarehouseInfos as WarehouseResource } from "models/PlanInfos";
import {
  ACTION_URL,
  baseUrlWarehouse,
  ELEMENT_URL,
  PLAN,
  sge as SGE_URL,
  STATUS_URL,
  WAREHOUSE_LIST_URL,
  LOCALISATION_URL,
  NOTIFICATION_URL,
} from "constants/api/routeGlobals";
import { LocationResponse } from "models/Responses/LocationResponse";
import { StatusResource } from "store/Slices/Status/statusSlice";
import {
  StatusResponse,
  transformStatusResponseListToResource,
} from "models/Responses/StatusResponse";
import {
  WarehouseResponse,
  transformWarehouseResponseToResource,
} from "models/Responses/WarehouseResponses";
import { LocationResource } from "models/Resources/LocationResource";
import { transformLocationResponseToResource } from "services/responseConverters/locationResponseToResource";
import { tagTypes } from "constants/api/Tags";
import {
  ActionRequestResponse,
  actionRequestResponseToResource as transformActionRequestResponseToResource,
} from "models/Responses/ActionRequest";
import {
  ElementDetailsResponse,
  transformElementDetailsResponseToResource,
} from "models/Responses/ElementDetailsResponse";
import { ElementDetailsResource } from "models/Resources/ElementDetailsResource";
import { RootState } from "store/store";
import { ElementContainerResource } from "models/Resources/ElementContainerResource";
import {
  ElementContainerResponse,
  transformElementContainerResponseToResource,
} from "models/Responses/ElementContainerPositionResponse";
import { NotificationResource } from "models/Resources/NotificationResource";
import {
  NotificationResponse,
  transformNotificationResponseToResource,
} from "models/Responses/NotificationResponse";
import { sortAndFilterNotifications } from "store/Slices/gridEventSlice";
import { transformResponseToErrorData } from "models/Errors/ErrorResponse";

export type BasicWarehouseQueryProps = {
  storeNb: number;
  elementId: string;
};

export type ElementDetailsActionQueryProps = {
  storeNb: number;
  elementId: string;
  action: string;
  token: string;
};

export type NotificationQueryProps = {
  storeNb: number;
  notificationId: string;
  action: string;
  token: string;
};

const apiKey = process.env.REACT_APP_API_KEY;

export const warehouseApi = createApi({
  reducerPath: "warehouseApi",
  baseQuery: fetchBaseQuery({
    baseUrl: baseUrlWarehouse,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).appState.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      headers.append("x-applicat-apikey", apiKey!);
      return headers;
    },
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: [
    tagTypes.WAREHOUSE_PLAN,
    tagTypes.STATUS,
    tagTypes.LOCATION,
    tagTypes.DETAIL,
    tagTypes.NOTIFICATION,
  ],

  endpoints: builder => ({
    getWarehousePlan: builder.query<WarehouseResource, number>({
      query: storeNb => ({
        url: SGE_URL + storeNb + "/" + PLAN,
        method: "GET",
      }),
      providesTags: () => [tagTypes.WAREHOUSE_PLAN],
      transformResponse: (warehouseResponse: WarehouseResponse) =>
        transformWarehouseResponseToResource(warehouseResponse),
    }),
    getStatusPlan: builder.query<StatusResource, number>({
      query: storeNb => ({
        url: SGE_URL + storeNb + "/" + STATUS_URL,
        method: "GET",
      }),
      providesTags: statusResource =>
        statusResource?.statusPlan
          ? [
              tagTypes.STATUS,
              ...statusResource.statusPlan.map(({ id }) => ({
                type: tagTypes.STATUS,
                id,
              })),
            ]
          : [],
      transformResponse: (statusResponse: StatusResponse) =>
        transformStatusResponseListToResource(statusResponse),
      transformErrorResponse: async response => {
        let actionError: FetchBaseQueryError;
        if (response) {
          const actionResponse = response;
          actionError = transformResponseToErrorData(actionResponse);
        } else {
          actionError = response;
        }
        return actionError;
      },
    }),
    getLocations: builder.query<LocationResource, void>({
      query: () => ({
        url: WAREHOUSE_LIST_URL,
        params: { plan: true },
        method: "GET",
      }),
      providesTags: () => [tagTypes.LOCATION],
      transformResponse: (locationResponse: LocationResponse[]) =>
        transformLocationResponseToResource(locationResponse),
      transformErrorResponse: async response => {
        let actionError: FetchBaseQueryError;
        if (response) {
          const actionResponse = response;
          actionError = transformResponseToErrorData(actionResponse);
        } else {
          actionError = response;
        }
        return actionError;
      },
    }),

    //prettier-ignore
    getElementContainerId: builder.query<ElementContainerResource, BasicWarehouseQueryProps>({
      query: props => ({
        url: SGE_URL + props.storeNb + "/" + ELEMENT_URL + "/" + props.elementId + "/" + LOCALISATION_URL,
        method: "GET",
      }),

      transformResponse: (
        elementContainerResponse: ElementContainerResponse
      ) => transformElementContainerResponseToResource(elementContainerResponse),
      transformErrorResponse: async (
        response) => {
        let actionError: FetchBaseQueryError;
        if (response) {
          const actionResponse = response;
          actionError = transformResponseToErrorData(actionResponse);
        } else {
          actionError = response;
        }
        return actionError;
      }
    }),

    //#region Notifications
    getNotificationList: builder.query<NotificationResource[], number>({
      query: storeNb => ({
        url: SGE_URL + storeNb + "/" + NOTIFICATION_URL,
        method: "GET",
      }),
      providesTags: () => [tagTypes.NOTIFICATION],
      transformResponse: (notificationResponse: NotificationResponse[]) => {
        const notificationsResources: NotificationResource[] = [];
        notificationResponse.forEach(notification => {
          notificationsResources.push(
            transformNotificationResponseToResource(notification)
          );
        });
        const hydratedNotifications: NotificationResource[] =
          sortAndFilterNotifications(notificationsResources);

        return hydratedNotifications;
      },
      transformErrorResponse: async response => {
        let actionError: FetchBaseQueryError;
        if (response) {
          const actionResponse = response;
          actionError = transformResponseToErrorData(actionResponse);
        } else {
          actionError = response;
        }
        return actionError;
      },
    }),

    //prettier-ignore
    postNotificationRead: builder.mutation<NotificationResponse, NotificationQueryProps>({
      query: props => ({
        url:
          SGE_URL +
          props.storeNb +
          "/" +
          NOTIFICATION_URL +
          "/" +
          props.notificationId,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(props.token ? { Authorization: `Bearer ${props.token}` } : {}),
        },
        body: props.action,
      }),
      invalidatesTags: notif => [
        { type: tagTypes.NOTIFICATION, id: notif?.notificationId },
      ],
      transformErrorResponse: async (
        response) => {
        let actionError: FetchBaseQueryError;
        if (response) {
          const actionResponse = response;
          actionError = transformResponseToErrorData(actionResponse);
        } else {
          actionError = response;
        }
        return actionError;
      }
    }),
    //#endregion

    //#region Fenêtre détails
    //prettier-ignore
    getElementDetails: builder.query<ElementDetailsResource, BasicWarehouseQueryProps>({
      query: props => ({
        url: SGE_URL + props.storeNb + "/" + ELEMENT_URL + "/" + props.elementId,
        method: "GET",
      }),
      transformResponse: (
        elementDetailsResponse: ElementDetailsResponse,
      ) => transformElementDetailsResponseToResource(elementDetailsResponse),
      transformErrorResponse: async (
        response,
      ) => {
        let actionError: FetchBaseQueryError;
        if (response) {
          const actionResponse = response;
          actionError = transformResponseToErrorData(actionResponse);
        } else {
          actionError = response;
        }
        return actionError;
      }
    }),

    //prettier-ignore
    postActionRequest: builder.mutation<ElementDetailsResource, ElementDetailsActionQueryProps>({
      query: props => ({
        headers: {
          "Content-Type": "application/json",
        },
        url:
          SGE_URL +
          props.storeNb +
          "/" +
          ELEMENT_URL +
          "/" +
          props.elementId +
          "/" +
          ACTION_URL,
        method: "POST",
        body: JSON.stringify({ action: props.action }),
      }),
      transformResponse: (
        elementActionRequestResponse: ActionRequestResponse,
      ) => transformActionRequestResponseToResource(elementActionRequestResponse),
      transformErrorResponse: async (
        response,
      ) => {
        let actionError: FetchBaseQueryError;
        if (response) {
          const actionResponse = response;
          actionError = transformResponseToErrorData(actionResponse);
        } else {
          actionError = response;
        }
        return actionError;
      }
    }),
    //#endregion
  }),
});

export const {
  useGetWarehousePlanQuery,
  useGetStatusPlanQuery,
  useGetLocationsQuery,
  useGetElementDetailsQuery,
  useGetNotificationListQuery,
  useLazyGetElementContainerIdQuery,
  usePostNotificationReadMutation,
  usePostActionRequestMutation,
} = warehouseApi;
